import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Users, Heart, Zap, Scale, Ban, AlertCircle, CheckCircle, Home } from 'lucide-react';
import CustomCursor from './CustomCursor';

const RulesPage = () => {
    const navigate = useNavigate();

    useEffect(() => {
        document.title = "Règlement du Serveur | PokeMovie - Serveur Cobblemon";
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
            metaDescription.setAttribute('content', "Règlement complet de PokeMovie : règles de comportement, sanctions, politique anti-triche. Serveur Cobblemon 1.21.1 français.");
        }
    }, []);

    const rulesSections = [
        {
            icon: <Heart className="w-8 h-8" />,
            title: "Respect & Comportement",
            color: "red",
            rules: [
                {
                    text: "Respecte tous les joueurs sans exception",
                    description: "Aucune forme de discrimination (race, religion, sexe, orientation, etc.) ne sera tolérée."
                },
                {
                    text: "Interdiction totale de harcèlement",
                    description: "Le harcèlement sous toutes ses formes (verbal, répété, cyber-harcèlement) entraîne un bannissement immédiat."
                },
                {
                    text: "Langage approprié dans le chat",
                    description: "Pas d'insultes, de vulgarités excessives, ou de spam. Le flood est limité à 3 messages max en 5 secondes."
                },
                {
                    text: "Pas de contenu explicite ou choquant",
                    description: "Aucun contenu pornographique, gore, ou traumatisant. Les skins doivent rester appropriés."
                }
            ]
        },
        {
            icon: <Shield className="w-8 h-8" />,
            title: "Triche & Avantages Déloyaux",
            color: "orange",
            rules: [
                {
                    text: "Interdiction de tout client modifié",
                    description: "Hacks, X-Ray, Fly, Speed hacks, Kill Aura, ou tout autre mod donnant un avantage déloyal = ban permanent."
                },
                {
                    text: "Pas d'exploitation de bugs",
                    description: "Si tu découvres un bug, reporte-le immédiatement. L'exploiter pour un gain personnel = sanction sévère."
                },
                {
                    text: "Interdiction d'auto-clickers et macros",
                    description: "Tout système automatisant des actions (farm AFK, combats auto, etc.) est strictement interdit."
                },
                {
                    text: "Pas de Pokémon hackés ou modifiés",
                    description: "Les Pokémon avec des stats impossibles, shinies instantanés suspects, ou obtenus par des moyens externes seront supprimés."
                }
            ]
        },
        {
            icon: <Users className="w-8 h-8" />,
            title: "Commerce & Échanges",
            color: "blue",
            rules: [
                {
                    text: "Pas d'arnaques en trading",
                    description: "Toute tentative d'arnaque (faux Pokémon, promesses non tenues, etc.) = ban + suppression des items."
                },
                {
                    text: "Interdiction de vente d'items réels",
                    description: "Aucune transaction impliquant de l'argent réel (PayPal, cartes cadeaux, etc.) n'est autorisée entre joueurs."
                },
                {
                    text: "Pas de spamming commercial",
                    description: "Tu peux vendre tes Pokémon/items, mais pas spammer le chat toutes les 30 secondes. Max 1 annonce toutes les 5 minutes."
                },
                {
                    text: "Les deals se font dans /trade",
                    description: "Pour ta sécurité, utilise TOUJOURS le système /trade officiel. Aucun remboursement pour les trades hors système."
                }
            ]
        },
        {
            icon: <Zap className="w-8 h-8" />,
            title: "Gameplay & Constructions",
            color: "yellow",
            rules: [
                {
                    text: "Pas de griefing",
                    description: "Détruire, voler, ou modifier les constructions/terrains d'autres joueurs sans permission = ban. Utilise /claim pour protéger."
                },
                {
                    text: "Limite de machines lag",
                    description: "Max 10 Redstone Clocks par claim. Les farms causant du lag excessif seront démantelées."
                },
                {
                    text: "Constructions appropriées",
                    description: "Pas de constructions offensantes, pornographiques, ou représentant des symboles de haine."
                },
                {
                    text: "Pas de claims abusifs",
                    description: "Ne claim pas des zones immenses sans les utiliser. Max 10 claims par joueur. Les claims inactifs 30+ jours seront supprimés."
                }
            ]
        },
        {
            icon: <Scale className="w-8 h-8" />,
            title: "PvP & Combats",
            color: "purple",
            rules: [
                {
                    text: "PvP consentement mutuel uniquement",
                    description: "Ne force JAMAIS quelqu'un au combat. Les zones sécurisées (spawn, market) sont PvP-free."
                },
                {
                    text: "Pas de kill steal",
                    description: "Si un joueur combat un Pokémon sauvage ou boss, ne le capture pas ou ne le tue pas. Respecte les engagements."
                },
                {
                    text: "Tournois : respect des règles",
                    description: "En ranked ou tournoi, respecte les clauses (Sleep Clause, Species Clause, etc.). Pas de rage quit."
                },
                {
                    text: "Pas de camping spawn",
                    description: "Tuer des joueurs qui viennent de spawn ou qui sont AFK = lâche et sanctionné."
                }
            ]
        },
        {
            icon: <AlertCircle className="w-8 h-8" />,
            title: "Comptes & Sécurité",
            color: "gray",
            rules: [
                {
                    text: "Un compte par personne",
                    description: "Les alts (comptes secondaires) sont interdits sauf autorisation staff pour test. Max 2 comptes par IP avec justification."
                },
                {
                    text: "Sécurise ton compte",
                    description: "Ne partage JAMAIS ton mot de passe. Le staff ne te le demandera JAMAIS. Active l'authentification 2FA si dispo."
                },
                {
                    text: "Pas d'usurpation d'identité",
                    description: "Ne te fais pas passer pour un staff, un autre joueur, ou une personnalité. Les pseudos similaires au staff sont interdits."
                },
                {
                    text: "Signale les problèmes",
                    description: "Si tu vois un tricheur, un bug, ou du harcèlement, utilise /report ou contacte un modérateur."
                }
            ]
        }
    ];

    const sanctions = [
        {
            severity: "Avertissement",
            color: "yellow",
            examples: "Spam léger, language inapproprié (1ère fois), flood chat",
            penalty: "Warning dans le log + rappel des règles"
        },
        {
            severity: "Mute Temporaire",
            color: "orange",
            examples: "Spam répété, insultes légères, pub non sollicitée",
            penalty: "1h à 7 jours selon gravité"
        },
        {
            severity: "Kick",
            color: "red",
            examples: "Comportement dérangeant persistant, refus de coopérer",
            penalty: "Expulsion du serveur, reconnexion possible"
        },
        {
            severity: "Ban Temporaire",
            color: "red",
            examples: "Griefing mineur, tentative de triche, harcèlement (1ère fois)",
            penalty: "3 jours à 30 jours selon gravité"
        },
        {
            severity: "Ban Permanent",
            color: "gray",
            examples: "Hacks, harcèlement sévère, contenu illégal, récidive",
            penalty: "Exclusion définitive du serveur + IP ban"
        }
    ];

    const colorMap = {
        red: "from-red-600 to-red-700",
        orange: "from-orange-600 to-orange-700",
        blue: "from-blue-600 to-blue-700",
        yellow: "from-yellow-600 to-yellow-700",
        purple: "from-purple-600 to-purple-700",
        gray: "from-gray-600 to-gray-700"
    };

    return (
        <div className="relative min-h-screen bg-white overflow-hidden">
            <CustomCursor />

            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800;900&family=Nunito:wght@400;600;700;800&display=swap');
      `}</style>

            {/* Navigation */}
            <motion.nav
                initial={{ y: -100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6 }}
                className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b-4 border-red-600 shadow-xl"
            >
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <motion.a
                        href="/"
                        className="flex items-center gap-2 px-6 py-3 rounded-full bg-red-600 text-white font-bold hover:bg-red-700 transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        <Home className="w-5 h-5" />
                        Retour Accueil
                    </motion.a>

                    <div className="flex items-center gap-4">
                        <div className="relative w-10 h-10">
                            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-red-600 to-red-700 shadow-2xl"></div>
                            <div className="absolute inset-0 top-0 h-1/2 rounded-t-full bg-red-600"></div>
                            <div className="absolute inset-0 bottom-0 top-1/2 h-1/2 rounded-b-full bg-white"></div>
                            <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gray-900 transform -translate-y-1/2"></div>
                            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full border border-gray-900"></div>
                        </div>
                        <div>
                            <div className="text-3xl font-black text-red-600" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                PokeMovie
                            </div>
                            <div className="text-xs font-bold text-gray-600 uppercase tracking-wider">Serveur Cobblemon</div>
                        </div>
                    </div>
                </div>
            </motion.nav>

            {/* Hero Section */}
            <section className="relative pt-32 pb-16 px-6 bg-gradient-to-br from-red-600 via-red-700 to-red-800">
                <div className="max-w-6xl mx-auto text-center relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <div className="text-8xl mb-6">⚖️</div>
                        <h1 className="text-5xl lg:text-7xl font-black text-white mb-6 leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Règlement du Serveur
                        </h1>
                        <p className="text-2xl text-red-100 font-semibold leading-relaxed max-w-3xl mx-auto">
                            Pour une expérience de jeu agréable et équitable pour tous, merci de respecter ces règles. L'ignorance du règlement ne sera pas une excuse.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Introduction */}
            <section className="py-16 px-6 bg-gray-50">
                <div className="max-w-6xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="bg-white rounded-3xl p-12 shadow-2xl border-4 border-red-100"
                    >
                        <h2 className="text-4xl font-black text-gray-900 mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            📋 Principes Fondamentaux
                        </h2>
                        <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
                            <p className="font-semibold">
                                <strong>PokeMovie</strong> est un serveur Cobblemon communautaire axé sur l'amusement, le respect mutuel, et la fair-play. Nous visons à créer un environnement sain où chacun peut profiter de sa passion pour les Pokémon.
                            </p>
                            <p>
                                Ce règlement est mis en place pour assurer que tous les joueurs puissent jouer dans les meilleures conditions. Les règles sont applicables à <strong>tous</strong>, sans exception (staff inclus).
                            </p>
                            <p className="font-bold text-red-600">
                                ⚠️ En jouant sur PokeMovie, tu acceptes automatiquement l'intégralité de ce règlement.
                            </p>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Rules Sections */}
            <section className="py-16 px-6">
                <div className="max-w-6xl mx-auto space-y-12">
                    {rulesSections.map((section, i) => (
                        <motion.div
                            key={section.title}
                            initial={{ opacity: 0, x: i % 2 === 0 ? -50 : 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: i * 0.1 }}
                            className="bg-white rounded-3xl p-8 lg:p-12 shadow-2xl border-4 border-gray-100"
                        >
                            <div className="flex items-center gap-4 mb-8">
                                <div className={`p-4 rounded-2xl bg-gradient-to-br ${colorMap[section.color]} text-white shadow-lg`}>
                                    {section.icon}
                                </div>
                                <h2 className="text-3xl lg:text-4xl font-black text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                    {section.title}
                                </h2>
                            </div>

                            <div className="space-y-6">
                                {section.rules.map((rule, j) => (
                                    <div key={j} className="flex gap-4">
                                        <div className="flex-shrink-0 mt-1">
                                            <CheckCircle className="w-6 h-6 text-green-600" />
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold text-gray-900 mb-2">
                                                {rule.text}
                                            </h3>
                                            <p className="text-gray-600 leading-relaxed">
                                                {rule.description}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Sanctions */}
            <section className="py-16 px-6 bg-gray-50">
                <div className="max-w-6xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        <div className="text-center mb-12">
                            <div className="text-7xl mb-6">⚠️</div>
                            <h2 className="text-5xl font-black text-gray-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                Système de Sanctions
                            </h2>
                            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-semibold">
                                Les sanctions sont appliquées en fonction de la gravité de l'infraction. Chaque cas est étudié individuellement par le staff.
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {sanctions.map((sanction, i) => (
                                <motion.div
                                    key={sanction.severity}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: i * 0.1 }}
                                    className="bg-white rounded-2xl p-6 shadow-xl border-4 border-gray-100"
                                >
                                    <div className={`w-full h-2 rounded-full bg-gradient-to-r ${colorMap[sanction.color]} mb-4`}></div>
                                    <h3 className="text-2xl font-black text-gray-900 mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                        {sanction.severity}
                                    </h3>
                                    <div className="space-y-3">
                                        <div>
                                            <p className="text-sm font-bold text-gray-500 mb-1">Exemples :</p>
                                            <p className="text-gray-700 text-sm">{sanction.examples}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-gray-500 mb-1">Sanction :</p>
                                            <p className="text-gray-900 font-bold text-sm">{sanction.penalty}</p>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* FAQ / Notes */}
            <section className="py-16 px-6">
                <div className="max-w-6xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl p-12 border-4 border-blue-100"
                    >
                        <h2 className="text-4xl font-black text-gray-900 mb-8" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            📌 Notes Importantes
                        </h2>
                        <div className="space-y-6 text-lg leading-relaxed">
                            <div className="flex gap-4">
                                <Ban className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                                <p className="text-gray-700">
                                    <strong>Contournement de ban :</strong> Toute tentative de contourner un ban (VPN, alt account, etc.) prolongera automatiquement ta sanction et pourra mener à un ban IP permanent.
                                </p>
                            </div>
                            <div className="flex gap-4">
                                <Shield className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                                <p className="text-gray-700">
                                    <strong>Appel de sanction :</strong> Si tu penses qu'une sanction est injuste, tu peux faire appel sur le Discord dans #appeals. Sois respectueux et fournis des preuves.
                                </p>
                            </div>
                            <div className="flex gap-4">
                                <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
                                <p className="text-gray-700">
                                    <strong>Modification du règlement :</strong> Ce règlement peut être modifié à tout moment. Les changements majeurs seront annoncés sur Discord et in-game.
                                </p>
                            </div>
                            <div className="flex gap-4">
                                <Users className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                                <p className="text-gray-700">
                                    <strong>Rôle du staff :</strong> Le staff a toujours le dernier mot. Leurs décisions doivent être respectées. En cas de désaccord, contacte un admin en privé, ne spam pas le chat.
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-16 px-6 bg-gradient-to-br from-red-600 via-red-700 to-red-800">
                <div className="max-w-5xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        <div className="text-7xl mb-6">✅</div>
                        <h2 className="text-5xl font-black text-white mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Prêt à Jouer ?
                        </h2>
                        <p className="text-2xl text-red-100 font-semibold mb-12 max-w-3xl mx-auto">
                            Maintenant que tu connais les règles, il est temps de rejoindre l'aventure PokeMovie et de devenir le meilleur dresseur !
                        </p>
                        <div className="flex flex-col sm:flex-row gap-6 justify-center">
                            <motion.button
                                onClick={() => navigate('/')}
                                className="px-10 py-5 rounded-2xl bg-white text-red-600 font-black text-xl hover:bg-yellow-400 hover:text-red-700 transition-all shadow-2xl"
                                style={{ fontFamily: 'Montserrat, sans-serif' }}
                                whileHover={{ scale: 1.05, y: -3 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                Retour Accueil
                            </motion.button>
                            <motion.a
                                href="https://discord.gg/3wS59h3Vbu"
                                target="_blank" rel="noreferrer"
                                className="px-10 py-5 rounded-2xl bg-gray-900 text-white font-black text-xl hover:bg-gray-800 transition-all shadow-2xl"
                                style={{ fontFamily: 'Montserrat, sans-serif' }}
                                whileHover={{ scale: 1.05, y: -3 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                Rejoindre Discord
                            </motion.a>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default RulesPage;